import { useCallback, useContext } from 'react';
import { IconButton, Link, Tooltip, Typography } from '@mui/material';
import { TEXTS } from '../../../core/texts';
import { ShadowContainer } from '../../common/ShadowContainer';
import { FlexBox, FlexBoxFullWidth } from '../../common/boxes';
import { MAX_RESUME_SIZE, RESUME_ALLOWED_FORMATS, RESUME_ALLOWED_FILE_EXT } from '../../../core/constraints';
import { getTruncatedName } from '../../../utils/getTruncatedName';
import WithUpload from '../../common/WithUpload';
import { FileUpload, OpenInNew, Upload } from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FreelancerService } from '../../../services/freelancer';
import { fileToBase64 } from '../../../utils/fileToBase64';
import { AxiosError } from 'axios';
import { IErrorResponse } from '../../../interfaces/errorResponse';
import { FreelancerContext } from './FreelancerEntity';
import {
  GET_FREELANCER_RESUME_QUERY_KEY,
  useGetFreelancerResume
} from '../../../queryHooks/freelancer/useGetFreelancerResume';
import { useNotification } from '../../../+state/useNotification';

const Resume = () => {
  const queryClient = useQueryClient();
  const { user } = useContext(FreelancerContext);
  const { data: resume, isLoading: isLoadingGetResume } = useGetFreelancerResume({ id: user.id });
  const { openSuccessToast, openErrorToast } = useNotification();

  const mutation = useMutation({
    mutationFn: async (file: File) => {
      const data = await fileToBase64(file);
      return FreelancerService.uploadResume(user.id, {
        fileName: file.name,
        fileContentBase64: data ? data.toString() : ''
      });
    },
    onSuccess: () => {
      openSuccessToast('Successfully uploaded');
      queryClient.invalidateQueries([GET_FREELANCER_RESUME_QUERY_KEY]);
    },
    onError: (err: AxiosError<IErrorResponse>) => {
      openErrorToast(err.response?.data.message || err.message);
    }
  });

  const handleUploadResume = useCallback((file: File) => mutation.mutate(file), [mutation]);

  return (
    <ShadowContainer width={'100%'}>

      <FlexBoxFullWidth justifyContent={'space-between'} mb={3}>
        <Typography variant={'h3'}>
          {TEXTS.CV}
        </Typography>
        {
          !resume &&
          <WithUpload
            accept={RESUME_ALLOWED_FORMATS}
            handleFileUpload={handleUploadResume}
            allowedExtensions={RESUME_ALLOWED_FILE_EXT}
            maxByteSize={MAX_RESUME_SIZE}
            isLoading={mutation.isLoading}
          >
            <Tooltip title={TEXTS.UPLOAD}>
              <IconButton color={'primary'}>
                <Upload/>
              </IconButton>
            </Tooltip>
          </WithUpload>
        }
      </FlexBoxFullWidth>
      {
        !resume && !isLoadingGetResume &&
        <Typography>
          {TEXTS.NOT_UPLOADED}
        </Typography>
      }
      {
        resume &&
        <FlexBoxFullWidth justifyContent={'space-between'} alignItems={'center'}>
          <Typography variant={'body2'} sx={{ wordBreak: 'break-word', mr: 2 }}>
            {getTruncatedName(resume.name, 150)}
          </Typography>
          <FlexBox flexDirection={{ mobile: 'column', tablet: 'row' }}>
            <WithUpload
              accept={RESUME_ALLOWED_FORMATS}
              handleFileUpload={handleUploadResume}
              allowedExtensions={RESUME_ALLOWED_FILE_EXT}
              maxByteSize={MAX_RESUME_SIZE}
              isLoading={mutation.isLoading}
            >
              <Tooltip title={TEXTS.RE_UPLOAD}>
                <IconButton color={'primary'}>
                  <FileUpload/>
                </IconButton>
              </Tooltip>
            </WithUpload>
            <Tooltip title={TEXTS.OPEN_IN_NEW_TAB}>
              <IconButton color={'primary'} component={Link} href={resume.src} target={'_blank'} rel={'noopener'}>
                <OpenInNew/>
              </IconButton>
            </Tooltip>
          </FlexBox>
        </FlexBoxFullWidth>
      }
    </ShadowContainer>
  );
};

export default Resume;